import Scene from '../Scene';
import common from '../../common';
import Text from '../../ui/Text';
import i18next from 'i18next';

export default class ReportScene extends Scene {
	constructor() {
		super();

		this._background = null;
		this._folder = null;
		this._page1 = null;
		this._scores = {};

		this._strings = {
			SCHOOL: common.scenario.targets.school > 1 ? i18next.t('gui.general.schools') : i18next.t('structure.school.description'),
			HOSPITAL: common.scenario.targets.hospital > 1 ? i18next.t('gui.general.hospitals') : i18next.t('structure.hospital.description'),
		};

		this._languageChangeSignal = common.languageManager.signals.changed.add(this.onLanguageChange, this);
	}

	init() {
		super.init();

		this.interactive = true;
		this.on('pointerup', () => {
			P3.audio.playSound('sfx_genericclick');
		});

		this._background = new PIXI.Graphics();

		this._background.beginFill(0x0, 0.6);
		this._background.drawRect(0, 0, common.STAGE_WIDTH, common.STAGE_HEIGHT);
		this._background.endFill();
		this._background.interactive = true;
		this.addChild(this._background);

		this._folder = new PIXI.Container();
		this._folder.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this._folder.scale.set(PIXI.utils.isMobile.apple.tablet ? 0.9 : 1);
		this._folder.scale.set(0.9);
		this.addChild(this._folder);

		const verdictPageLeft = new PIXI.Sprite(P3.assets.texture('verdict_folder'));

		verdictPageLeft.anchor.set(1, 0.5);
		verdictPageLeft.position.set(0, 0);
		this._folder.addChild(verdictPageLeft);

		const verdictPageRight = new PIXI.Sprite(P3.assets.texture('verdict_folder'));

		verdictPageRight.anchor.set(1, 0.5);
		verdictPageRight.position.set(0, 0);
		verdictPageRight.scale.x = -1;
		this._folder.addChild(verdictPageRight);

		const upperEdge = this._folder.height * (1 / this._folder.scale.y) * -0.5;

		const classifiedLogo = new PIXI.Sprite(P3.assets.texture('logo_classified'));

		classifiedLogo.anchor.set(0.5);
		classifiedLogo.position.set(verdictPageRight.width - 200, upperEdge + 130);
		this._folder.addChild(classifiedLogo);

		const classifiedText = new Text('gui.reportscene.classified', { font: '20px', fontWeight: 'bold', align: 'center', tint: 0x0 });

		classifiedText.anchor.set(0.5);
		classifiedText.position.set(verdictPageRight.width - 200, upperEdge + 180);
		this._folder.addChild(classifiedText);

		const dividerT = new PIXI.Sprite(P3.assets.texture('divider'));

		dividerT.width = 1024;
		dividerT.alpha = 0.3;
		dividerT.position.set(dividerT.width * -0.5, upperEdge + 200);
		this._folder.addChild(dividerT);

		const dividerB = new PIXI.Sprite(P3.assets.texture('divider'));

		dividerB.width = 1024;
		dividerB.alpha = 0.3;
		dividerB.position.set(dividerB.width * -0.5, upperEdge + 205);
		this._folder.addChild(dividerB);

		this._folder.page1 = this._createPage1();
		this._folder.page1.visible = true;
		this._folder.page1.position.y = 0;
		this._folder.addChild(this._folder.page1);

		this._folder.page2 = this._createPage2();
		this._folder.page2.visible = false;
		this._folder.page2.position.y = 0;
		this._folder.addChild(this._folder.page2);

		this._scores = {
			bDestroyed: common.scenario.disaster.totalDestroyed,
			pHoused: common.scenario.population.housed,
			pHousedTarget: common.scenario.targets.housing,
			pShelter: common.scenario.population.sheltered,
			damage: common.scenario.disaster.totalCost,
			deaths: common.scenario.disaster.totalDeaths,
			injuries: common.scenario.disaster.totalInjury,
			missions: {
				hospitals: common.scenario.targetProgress.hospital,
				mission: common.scenario.targetProgress.mission,
				schools: common.scenario.targetProgress.school,
			},
			budget: common.scenario.budget.remaining,
			facts: common.achievements.unlocked.length,
			score: common.score,
		};
		this.set(this._scores, common.scenario.win);
		this.appear();
	}

	onLanguageChange() {
		this._strings.SCHOOL = common.scenario.targets.school > 1 ? i18next.t('gui.general.schools') : i18next.t('structure.school.description');
		this._strings.HOSPITAL = common.scenario.targets.hospital > 1 ? i18next.t('gui.general.hospitals') : i18next.t('structure.hospital.description');

		this._folder.page1.categories.mission.schools.text = i18next.t('gui.reportscene.page1.mission.schools', this._strings);
		this._folder.page1.categories.mission.hospitals.text = i18next.t('gui.reportscene.page1.mission.hospital', this._strings);

		this._folder.page2.body.content.text = i18next.t('gui.reportscene.page2.body', { ...this._strings, SCORE: common.numberWithCommas(this._scores.score) });
	}

	_createPage1() {
		const leftEdge = -560;
		const valueOffset = 550;
		const rightEdge = 10;

		const page = new PIXI.Container();

		page.categories = new PIXI.Container();
		page.addChild(page.categories);

		/** HOUSING **/
		page.categories.housing = new PIXI.Container();
		page.categories.addChild(page.categories.housing);

		page.categories.housing.title = new Text('gui.reportscene.page1.housing.title', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		page.categories.housing.title.anchor.set(0, 0.5);
		page.categories.housing.title.position.set(leftEdge, -150);
		page.categories.housing.addChild(page.categories.housing.title);

		page.categories.housing.destroyedLabel = new Text('gui.reportscene.page1.housing.destroyed', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.housing.destroyedLabel.anchor.set(0, 0.5);
		page.categories.housing.destroyedLabel.position.set(leftEdge, -115);
		page.categories.housing.addChild(page.categories.housing.destroyedLabel);

		page.categories.housing.destroyedLabel.value = new Text('', { font: '28px', align: 'center', tint: 0x0 });
		page.categories.housing.destroyedLabel.value.anchor.set(1, 0.5);
		page.categories.housing.destroyedLabel.value.position.set(valueOffset, 0);
		page.categories.housing.destroyedLabel.addChild(page.categories.housing.destroyedLabel.value);

		page.categories.housing.housed = new Text('gui.reportscene.page1.housing.housed', { font: '28px', align: 'center', tint: 0x0 });
		page.categories.housing.housed.anchor.set(0, 0.5);
		page.categories.housing.housed.position.set(leftEdge, -85);
		page.categories.housing.addChild(page.categories.housing.housed);

		page.categories.housing.housed.value = new Text('', { font: '28px', align: 'center', tint: 0x0 });
		page.categories.housing.housed.value.anchor.set(1, 0.5);
		page.categories.housing.housed.value.position.set(valueOffset, 0);
		page.categories.housing.housed.addChild(page.categories.housing.housed.value);

		page.categories.housing.housed.popIcon = new PIXI.Sprite(P3.assets.texture('icon_person'));
		page.categories.housing.housed.popIcon.tint = 0x0;
		page.categories.housing.housed.popIcon.anchor.set(1, 0.5);
		page.categories.housing.housed.popIcon.position.set(valueOffset, 0);
		page.categories.housing.housed.addChild(page.categories.housing.housed.popIcon);

		page.categories.housing.sheltered = new Text('gui.reportscene.page1.housing.sheltered', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.housing.sheltered.anchor.set(0, 0.5);
		page.categories.housing.sheltered.position.set(leftEdge, -55);
		page.categories.housing.addChild(page.categories.housing.sheltered);

		page.categories.housing.sheltered.value = new Text('', { font: '28px', align: 'center', tint: 0x0 });
		page.categories.housing.sheltered.value.anchor.set(1, 0.5);
		page.categories.housing.sheltered.value.position.set(valueOffset, 0);
		page.categories.housing.sheltered.addChild(page.categories.housing.sheltered.value);

		page.categories.housing.sheltered.popIcon = new PIXI.Sprite(P3.assets.texture('icon_person'));
		page.categories.housing.sheltered.popIcon.tint = 0x0;
		page.categories.housing.sheltered.popIcon.anchor.set(1, 0.5);
		page.categories.housing.sheltered.popIcon.position.set(valueOffset, 0);
		page.categories.housing.sheltered.addChild(page.categories.housing.sheltered.popIcon);

		page.categories.housing.damages = new Text('gui.reportscene.page1.housing.damages', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.housing.damages.anchor.set(0, 0.5);
		page.categories.housing.damages.position.set(rightEdge, -115);
		page.categories.housing.addChild(page.categories.housing.damages);

		page.categories.housing.damages.value = new Text('', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.housing.damages.value.anchor.set(1, 0.5);
		page.categories.housing.damages.value.position.set(valueOffset, 0);
		page.categories.housing.damages.addChild(page.categories.housing.damages.value);

		page.categories.housing.deaths = new Text('gui.reportscene.page1.housing.death', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.housing.deaths.anchor.set(0, 0.5);
		page.categories.housing.deaths.position.set(rightEdge, -85);
		page.categories.housing.addChild(page.categories.housing.deaths);

		page.categories.housing.deaths.value = new Text('', { font: '28px', align: 'center', tint: 0x0 });
		page.categories.housing.deaths.value.anchor.set(1, 0.5);
		page.categories.housing.deaths.value.position.set(valueOffset, 0);
		page.categories.housing.deaths.addChild(page.categories.housing.deaths.value);

		page.categories.housing.deaths.popIcon = new PIXI.Sprite(P3.assets.texture('icon_person'));
		page.categories.housing.deaths.popIcon.tint = 0x0;
		page.categories.housing.deaths.popIcon.anchor.set(1, 0.5);
		page.categories.housing.deaths.popIcon.position.set(valueOffset, 0);
		page.categories.housing.deaths.addChild(page.categories.housing.deaths.popIcon);

		page.categories.housing.injured = new Text('gui.reportscene.page1.housing.injury', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.housing.injured.anchor.set(0, 0.5);
		page.categories.housing.injured.position.set(rightEdge, -55);
		page.categories.housing.addChild(page.categories.housing.injured);

		page.categories.housing.injured.value = new Text('', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.housing.injured.value.anchor.set(1, 0.5);
		page.categories.housing.injured.value.position.set(valueOffset, 0);
		page.categories.housing.injured.addChild(page.categories.housing.injured.value);

		page.categories.housing.injured.popIcon = new PIXI.Sprite(P3.assets.texture('icon_person'));
		page.categories.housing.injured.popIcon.tint = 0x0;
		page.categories.housing.injured.popIcon.anchor.set(1, 0.5);
		page.categories.housing.injured.popIcon.position.set(valueOffset, 0);
		page.categories.housing.injured.addChild(page.categories.housing.injured.popIcon);

		page.categories.housing.divider = new PIXI.Sprite(P3.assets.texture('divider'));
		page.categories.housing.divider.position.set(leftEdge, -25);
		page.categories.housing.divider.width = valueOffset;
		page.categories.housing.divider.alpha = 0.3;
		page.categories.housing.addChild(page.categories.housing.divider);

		page.categories.housing.dividerR = new PIXI.Sprite(P3.assets.texture('divider'));
		page.categories.housing.dividerR.position.set(rightEdge, -25);
		page.categories.housing.dividerR.width = valueOffset;
		page.categories.housing.dividerR.alpha = 0.3;
		page.categories.housing.addChild(page.categories.housing.dividerR);

		/** MISSION **/
		page.categories.mission = new PIXI.Container();
		page.categories.mission.y += 120;
		page.categories.addChild(page.categories.mission);

		page.categories.mission.title = new Text('gui.reportscene.page1.mission.title', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		page.categories.mission.title.anchor.set(0, 0.5);
		page.categories.mission.title.position.set(leftEdge, -120);
		page.categories.mission.addChild(page.categories.mission.title);

		const school = i18next.t('gui.reportscene.page1.mission.schools', this._strings);

		page.categories.mission.schools = new Text(school, { font: '28px', align: 'left', tint: 0x0 });
		page.categories.mission.schools.anchor.set(0, 0.5);
		page.categories.mission.schools.position.set(leftEdge, -85);
		page.categories.mission.addChild(page.categories.mission.schools);

		page.categories.mission.schools.value = new Text('', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.mission.schools.value.anchor.set(1, 0.5);
		page.categories.mission.schools.value.position.set(valueOffset, 0);
		page.categories.mission.schools.addChild(page.categories.mission.schools.value);

		const hospital = i18next.t('gui.reportscene.page1.mission.hospital', this._strings);

		page.categories.mission.hospitals = new Text(hospital, { font: '28px', align: 'left', tint: 0x0 });
		page.categories.mission.hospitals.anchor.set(0, 0.5);
		page.categories.mission.hospitals.position.set(leftEdge, -55);
		page.categories.mission.addChild(page.categories.mission.hospitals);

		page.categories.mission.hospitals.value = new Text('', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.mission.hospitals.value.anchor.set(1, 0.5);
		page.categories.mission.hospitals.value.position.set(valueOffset, 0);
		page.categories.mission.hospitals.addChild(page.categories.mission.hospitals.value);

		page.categories.mission.apartments = new Text('gui.reportscene.page1.mission.hotels', { font: '28px', align: 'left', tint: 0x0, maxWidth: 320 });
		page.categories.mission.apartments.anchor.set(0);
		page.categories.mission.apartments.position.set(rightEdge, -85 - page.categories.mission.apartments.height * 0.5);
		page.categories.mission.addChild(page.categories.mission.apartments);

		page.categories.mission.apartments.value = new Text('', { font: '28px', align: 'right', tint: 0x0, maxWidth: 320 });
		page.categories.mission.apartments.value.anchor.set(1, 0);
		page.categories.mission.apartments.value.position.set(valueOffset, 0);
		page.categories.mission.apartments.addChild(page.categories.mission.apartments.value);

		page.categories.mission.divider = new PIXI.Sprite(P3.assets.texture('divider'));
		page.categories.mission.divider.position.set(leftEdge, -25);
		page.categories.mission.divider.width = valueOffset;
		page.categories.mission.divider.alpha = 0.3;
		page.categories.mission.addChild(page.categories.mission.divider);

		page.categories.mission.dividerR = new PIXI.Sprite(P3.assets.texture('divider'));
		page.categories.mission.dividerR.position.set(rightEdge, -25);
		page.categories.mission.dividerR.width = valueOffset;
		page.categories.mission.dividerR.alpha = 0.3;
		page.categories.mission.addChild(page.categories.mission.dividerR);

		/** BONUS **/
		page.categories.bonus = new PIXI.Container();
		page.categories.bonus.y += 220;
		page.categories.addChild(page.categories.bonus);

		page.categories.bonus.title = new Text('gui.reportscene.page1.bonus.title', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		page.categories.bonus.title.anchor.set(0, 0.5);
		page.categories.bonus.title.position.set(leftEdge, -100);
		page.categories.bonus.addChild(page.categories.bonus.title);

		page.categories.bonus.facts = new Text('gui.reportscene.page1.bonus.facts', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.bonus.facts.anchor.set(0, 0.5);
		page.categories.bonus.facts.position.set(leftEdge, -65);
		page.categories.bonus.addChild(page.categories.bonus.facts);

		page.categories.bonus.facts.value = new Text('', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.bonus.facts.value.anchor.set(1, 0.5);
		page.categories.bonus.facts.value.position.set(valueOffset, 0);
		page.categories.bonus.facts.addChild(page.categories.bonus.facts.value);

		page.categories.bonus.budget = new Text('gui.reportscene.page1.bonus.budget', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.bonus.budget.anchor.set(0, 0.5);
		page.categories.bonus.budget.position.set(rightEdge, -65);
		page.categories.bonus.addChild(page.categories.bonus.budget);

		page.categories.bonus.budget.value = new Text('', { font: '28px', align: 'left', tint: 0x0 });
		page.categories.bonus.budget.value.anchor.set(1, 0.5);
		page.categories.bonus.budget.value.position.set(valueOffset, 0);
		page.categories.bonus.budget.addChild(page.categories.bonus.budget.value);

		/** FOOTER **/
		page.footer = new PIXI.Container();
		page.footer.y = 200;
		page.addChild(page.footer);

		const continueButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_blue_fill_large_9slice_out', width: 260, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_blue_fill_large_9slice_over', width: 260, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			icon: P3.assets.texture('icon_arrow_r'),
		});

		continueButton.position.set(115 + continueButton.width * 0.5, continueButton.height * 0.5);
		continueButton.signals.click.add(this.switchPage, this);
		page.footer.addChild(continueButton);
		page.footer.continueButton = continueButton;
		continueButton._icon.position.x = continueButton.width * 0.5 - 40;

		const textLabel = new Text('gui.reportscene.footer.buttons.continue', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });

		textLabel.anchor.set(0, 0.5);
		textLabel.position.set(continueButton.width * -0.5 + 40, 0);
		continueButton.animation.addChild(textLabel);

		continueButton.accessibleTitle = textLabel.text;

		page.footer.score = new Text('gui.reportscene.page1.bonus.score', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		page.footer.score.anchor.set(0, 0.5);
		page.footer.score.position.set(leftEdge, page.footer.continueButton.height * 0.5);
		page.footer.addChild(page.footer.score);

		page.footer.score.value = new Text('', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		page.footer.score.value.anchor.set(1, 0.5);
		page.footer.score.value.position.set(valueOffset, 0);
		page.footer.score.addChild(page.footer.score.value);

		page.footer.dividerR = new PIXI.Sprite(P3.assets.texture('divider'));
		page.footer.dividerR.position.set(30, -20);
		page.footer.dividerR.width = valueOffset;
		page.footer.dividerR.alpha = 0.3;
		page.footer.addChild(page.footer.dividerR);

		page.footer.dividerL = new PIXI.Sprite(P3.assets.texture('divider'));
		page.footer.dividerL.position.set(leftEdge, -20);
		page.footer.dividerL.width = valueOffset;
		page.footer.dividerL.alpha = 0.3;
		page.footer.addChild(page.footer.dividerL);

		return page;
	}

	switchPage() {
		this._folder.page1.visible = false;
		this._folder.page2.visible = true;

		if (common.scenario.win) {
			P3.audio.playSound('sfx_mission_success_00');
		} else {
			P3.audio.playSound('sfx_mission_failed_00');
		}
	}

	_createPage2() {
		const page = new PIXI.Container();

		page.header = new Text('gui.reportscene.page2.fail', { font: '128px', fontWeight: 'bold', align: 'left', tint: 0xC21313 });
		page.header.pivot.set(0.5);
		page.header.rotation = -0.04;
		page.header.anchor.set(0.5);
		page.header.position.set(0, -100);
		page.addChild(page.header);

		page.body = new PIXI.Container();
		page.addChild(page.body);

		page.body.header = new Text('gui.reportscene.page2.header.bad', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		page.body.header.anchor.set(0.5, 0.5);
		page.body.header.position.set(0, 0);
		page.body.addChild(page.body.header);

		page.body.content = new Text('gui.reportscene.page2.body', { font: '32px', align: 'left', tint: 0x0, maxWidth: 800 });
		page.body.content.anchor.set(0.5, 0);
		page.body.content.position.set(0, 25);
		page.body.addChild(page.body.content);

		page.footer = new PIXI.Container();
		page.footer.y = 200;
		page.addChild(page.footer);

		page.footer.divider = new PIXI.Sprite(P3.assets.texture('divider'));
		page.footer.divider.anchor.set(0.5);
		page.footer.divider.width = 830;
		page.footer.divider.alpha = 0.3;
		page.footer.divider.y = -20;
		page.footer.addChild(page.footer.divider);

		const menuButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_black_outline_9slice_out', width: 220, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_black_outline_9slice_over', width: 220, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			icon: P3.assets.texture('icon_menu'),
		});

		menuButton.position.set(-200, menuButton.height * 0.5);
		menuButton.signals.click.once(() => {
			common.started = false;
			common.lastBuild = null;
			common.disaster = null;

			common.scenario.stopSounds();

			this.signals.previous.dispatch();
		}, this);
		menuButton._icon.position.x = menuButton.width * 0.5 - 40;
		page.footer.addChild(menuButton);
		page.footer.menuButton = menuButton;

		let label = new Text('gui.reportscene.footer.buttons.menu', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0x0, maxWidth: 130 });

		label.anchor.set(0.5);
		label.position.set(-20, 0);
		menuButton.animation.addChild(label);

		menuButton.accessibleTitle = label.text;

		const repeatButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_blue_fill_large_9slice_out', width: 220, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_blue_fill_large_9slice_over', width: 220, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			icon: P3.assets.texture('icon_replay'),
		});

		repeatButton.position.set(200, repeatButton.height * 0.5);
		repeatButton.signals.click.once(() => {
			common.tracking.track(new P3.TrackingDataGtagEvent('retry', {
				event_category: 'UI',
				event_type: `retry_${common.difficulty}`,
				page_container: common.selectedLevel,
			}));

			common.started = false;
			common.disaster = null;
			common.lastBuild = null;

			common.scenario.stopSounds();

			this.signals.next.dispatch();
		}, this);
		repeatButton._icon.position.x = repeatButton.width * 0.5 - 40;
		page.footer.addChild(repeatButton);
		page.footer.repeatButton = repeatButton;

		label = new Text('gui.reportscene.footer.buttons.repeat', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF, maxWidth: 130 });
		label.anchor.set(0.5);
		label.position.set(-20, 0);
		repeatButton.animation.addChild(label);

		repeatButton.accessibleTitle = label.text;

		// SECOND ROW vertical offset (below first-row buttons)
		const rowYOffset = repeatButton.height * 0.5 + 90;
		const secondRowYOffset = rowYOffset + 100; // push into black area below the card
		const secondRowXSpacing = 320; // horizontal spacing between 2nd-row buttons

		// CERTIFICATE BUTTON (tsunami wins only)
		// see: https://gitlab.com/undrr/web-backlog/-/issues/2398
		const certificateButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_blue_fill_large_9slice_out', width: 280, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_blue_fill_large_9slice_over', width: 280, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			icon: P3.assets.texture('icon_arrow_r'),
		});

		certificateButton.position.set(0, secondRowYOffset);
		certificateButton.visible = false;
		certificateButton.signals.click.add(() => {
			const langCode = encodeURIComponent(common.config.language || 'en');
			const scenarioSlug = (common.selectedLevel || '').toLowerCase();
			const score = Number.isFinite(common.score) ? common.score : 0;
			const ts = Math.floor(Date.now() / 1000);
			const url = `${location.origin}/certificate/?lang=${langCode}&scenario=${encodeURIComponent(scenarioSlug || 'unknown')}&score=${encodeURIComponent(score)}&ts=${encodeURIComponent(ts)}`;

			window.open(url, '_blank');
		}, this);
		certificateButton._icon.position.x = certificateButton.width * 0.5 - 40;
		page.footer.addChild(certificateButton);
		page.footer.certificateButton = certificateButton;

		const certLabel = new Text('gui.reportscene.footer.buttons.certificate', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF, maxWidth: 220 });

		certLabel.anchor.set(0.5);
		certLabel.position.set(-20, 0);
		certificateButton.animation.addChild(certLabel);
		certificateButton.accessibleTitle = certLabel.text;

		// SECOND ROW: PreventionWeb learn-more and post-game survey
		const learnMoreButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_blue_fill_large_9slice_out', width: 280, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_blue_fill_large_9slice_over', width: 280, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			icon: P3.assets.texture('icon_arrow_r'),
		});

		learnMoreButton.position.set(-secondRowXSpacing, secondRowYOffset);
		learnMoreButton.signals.click.add(() => {
			const scenarioSlug = (common.selectedLevel || '').toLowerCase();
			const hazardLinks = {
				tsunami: 'https://www.preventionweb.net/understanding-disaster-risk',
				earthquake: 'https://www.preventionweb.net/understanding-disaster-risk',
				flood: 'https://www.preventionweb.net/understanding-disaster-risk',
				wildfire: 'https://www.preventionweb.net/understanding-disaster-risk',
				hurricane: 'https://www.preventionweb.net/understanding-disaster-risk',
			};

			const baseUrl = hazardLinks[scenarioSlug] || 'https://www.preventionweb.net/stop-disasters-game/get-involved';
			const url = `${baseUrl}?utm_source=stopdisastersgame&utm_medium=web&utm_campaign=stopdisasters_learn_more&utm_content=${encodeURIComponent(scenarioSlug || 'unknown')}`;

			window.open(url, '_blank');
		}, this);
		learnMoreButton._icon.position.x = learnMoreButton.width * 0.5 - 40;
		page.footer.addChild(learnMoreButton);
		page.footer.learnMoreButton = learnMoreButton;

		const learnMoreLabel = new Text('gui.reportscene.footer.buttons.learnMore', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF, maxWidth: 230 });

		learnMoreLabel.anchor.set(0.5);
		learnMoreLabel.position.set(-13, 0);
		learnMoreButton.animation.addChild(learnMoreLabel);
		learnMoreButton.accessibleTitle = learnMoreLabel.text;

		const surveyButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_blue_fill_large_9slice_out', width: 280, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_blue_fill_large_9slice_over', width: 280, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			icon: P3.assets.texture('icon_arrow_r'),
		});

		surveyButton.position.set(secondRowXSpacing, secondRowYOffset);
		surveyButton.signals.click.add(() => {
			const scenarioSlug = (common.selectedLevel || '').toLowerCase();
			const url = `https://www.preventionweb.net/stopdisasters-survey?utm_source=stopdisastersgame&utm_medium=web&utm_campaign=stopdisasters_survey&utm_content=${encodeURIComponent(scenarioSlug || 'unknown')}`;

			window.open(url, '_blank');
		}, this);
		surveyButton._icon.position.x = surveyButton.width * 0.5 - 40;
		page.footer.addChild(surveyButton);
		page.footer.surveyButton = surveyButton;

		const surveyLabel = new Text('gui.reportscene.footer.buttons.survey', { font: '30px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF, maxWidth: 230 });

		surveyLabel.anchor.set(0.5);
		surveyLabel.position.set(-20, 0);
		surveyButton.animation.addChild(surveyLabel);
		surveyButton.accessibleTitle = surveyLabel.text;

		return page;
	}

	set(scores, hasWon) {
		const highlightScore = (label) => {
			const highlight = new PIXI.Graphics();

			highlight.beginFill(0xffabab, 0.5);
			highlight.drawRect(0, 0, 560, label.height);
			highlight.endFill();
			label.parent.addChildAt(highlight, label.parent.getChildIndex(label) - 1);
			highlight.position.set(label.x - 5, label.y + (label.height * -(label.anchor.y)));
		};

		const cat = this._folder.page1.categories;

		cat.housing.destroyedLabel.value.text = scores.bDestroyed;
		cat.housing.housed.value.text = `${scores.pHoused}/${scores.pHousedTarget}`;
		cat.housing.sheltered.value.text = scores.pShelter;
		cat.housing.damages.value.text = `$${common.numberWithCommas(scores.damage)}`;
		cat.housing.deaths.value.text = scores.deaths;
		cat.housing.injured.value.text = scores.injuries;

		cat.housing.housed.popIcon.position.set(cat.housing.housed.value.x - cat.housing.housed.value.width - 5, 0);
		cat.housing.sheltered.popIcon.position.set(cat.housing.sheltered.value.x - cat.housing.sheltered.value.width - 5, 0);
		cat.housing.deaths.popIcon.position.set(cat.housing.deaths.value.x - cat.housing.deaths.value.width - 5, 0);
		cat.housing.injured.popIcon.position.set(cat.housing.injured.value.x - cat.housing.injured.value.width - 5, 0);

		cat.mission.schools.value.text = scores.missions.schools ? common.scenario.disaster.schoolDestroyed ? 'gui.reportscene.page2.destroyed' : 'gui.reportscene.page2.pass' : 'gui.reportscene.page2.fail';
		cat.mission.hospitals.value.text = scores.missions.hospitals ? common.scenario.disaster.hospitalDestroyed ? 'gui.reportscene.page2.destroyed' : 'gui.reportscene.page2.pass' : 'gui.reportscene.page2.fail';
		cat.mission.apartments.value.text = scores.missions.mission ? 'gui.reportscene.page2.pass' : 'gui.reportscene.page2.fail';

		if (common.selectedLevel === 'wildfire') {
			cat.mission.apartments.text = 'gui.overviewpanel.goals.hotel.value_wf';
		} else if (common.selectedLevel === 'earthquake') {
			cat.mission.apartments.text = i18next.t('gui.overviewpanel.goals.hotel.value_eq', { NUMBER: common.scenario.targets.mission });
		} else if (common.selectedLevel === 'flood') {
			cat.mission.apartments.text = 'gui.overviewpanel.goals.hotel.value_fd';
		} else if (common.selectedLevel === 'hurricane') {
			cat.mission.apartments.text = 'gui.overviewpanel.goals.hotel.value_hr';
		}

		if (!scores.missions.schools || common.scenario.disaster.schoolDestroyed) {
			highlightScore(cat.mission.schools);
		}
		if (!scores.missions.hospitals || common.scenario.disaster.hospitalDestroyed) {
			highlightScore(cat.mission.hospitals);
		}
		if (!scores.missions.mission) {
			highlightScore(cat.mission.apartments);
		}
		if (scores.pHoused !== scores.pHousedTarget) {
			highlightScore(cat.housing.housed);
		}
		if (scores.damage > common.scenario.limits.damage) {
			highlightScore(cat.housing.damages);
		}

		if (common.scenario.disaster.schoolDestroyed) {
			scores.score -= 1000;
		}
		if (common.scenario.disaster.hospitalDestroyed) {
			scores.score -= 1000;
		}

		cat.bonus.facts.value.text = scores.facts;
		cat.bonus.budget.value.text = `$${common.numberWithCommas(scores.budget)}`;
		this._folder.page1.footer.score.value.text = common.numberWithCommas(scores.score);

		if (hasWon) {
			this._folder.page2.header.text = 'gui.reportscene.page2.win';
			this._folder.page2.header.style.fill = 0x57d13c;

			this._folder.page2.body.header.text = 'gui.reportscene.page2.header.good';
			this._folder.page2.body.content.text = 'gui.reportscene.page2.body.good';
			this._folder.page2.body.content.text = this._folder.page2.body.content.text.replace('{{SCORE}}', common.numberWithCommas(scores.score));

			if (this._folder.page2.footer && this._folder.page2.footer.certificateButton) {
				this._folder.page2.footer.certificateButton.visible = true;
			}
		} else {
			this._folder.page2.header.text = 'gui.reportscene.page2.fail';
			this._folder.page2.body.header.text = 'gui.reportscene.page2.header.bad';
			this._folder.page2.body.content.text = 'gui.reportscene.page2.body.bad';
		}
	}

	appear() {
		super.appear();

		const categories = this._folder.page1.categories.children;

		for (let i = 0; i < categories.length; i++) {
			categories[i].visible = false;
			categories[i].scale.set(1.2, 1.2);
			categories[i].alpha = 0.5;
			new TWEEN.Tween(categories[i].scale, this.tweens)
				.to({
					x: 1,
					y: 1,
				}, 500)
				.easing(TWEEN.Easing.Quartic.Out)
				.delay(i * 1000)
				.onStart(() => {
					categories[i].visible = true;
				})
				.start();

			new TWEEN.Tween(categories[i], this.tweens)
				.to({
					alpha: 1,
				}, 300)
				.easing(TWEEN.Easing.Quartic.Out)
				.delay(i * 1000)
				.start();
		}
	}

	destroy() {
		common.languageManager.signals.changed.detach(this._languageChangeSignal, this);

		super.destroy();
	}

	resize() {
		super.resize();
		this._folder.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this._background.width = common.STAGE_WIDTH;
		this._background.height = common.STAGE_HEIGHT;
	}

	update() {
		super.update();
	}
}
